import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { getPlatformSettings } from '../services/firestoreService';
import {
  DEFAULT_THEME_ID,
  PLATFORM_THEME_CACHE_KEY,
  THEME_STORAGE_KEY,
  getTheme,
  isThemeId,
} from '../config/themes';

const readCachedPlatformTheme = () => {
  try {
    const cached = localStorage.getItem(PLATFORM_THEME_CACHE_KEY);
    return cached && isThemeId(cached) ? cached : DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
};

const cachePlatformTheme = (themeId) => {
  try {
    localStorage.setItem(PLATFORM_THEME_CACHE_KEY, themeId);
  } catch {
    /* ignore */
  }
};

const ThemeContext = createContext(null);

const applyThemeToDocument = (themeId) => {
  const id = isThemeId(themeId) ? themeId : DEFAULT_THEME_ID;
  document.documentElement.setAttribute('data-theme', id);
  const meta = document.querySelector('meta[name="theme-color"]');
  const theme = getTheme(id);
  if (meta && theme) {
    const styles = getComputedStyle(document.documentElement);
    const themeColor = styles.getPropertyValue('--pf-theme-color').trim();
    if (themeColor) meta.setAttribute('content', themeColor);
  }
  return id;
};

export const ThemeProvider = ({ children }) => {
  const [platformThemeId, setPlatformThemeId] = useState(readCachedPlatformTheme);
  const [previewThemeId, setPreviewThemeId] = useState(null);
  const [userThemeId, setUserThemeId] = useState(() => {
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY);
      return stored && isThemeId(stored) ? stored : null;
    } catch {
      return null;
    }
  });
  const [ready, setReady] = useState(false);

  const activeThemeId = previewThemeId || userThemeId || platformThemeId || DEFAULT_THEME_ID;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const result = await getPlatformSettings();
        if (mounted && result.success && result.settings?.defaultThemeId) {
          const id = result.settings.defaultThemeId;
          if (isThemeId(id)) {
            setPlatformThemeId(id);
            cachePlatformTheme(id);
          }
        }
      } catch (_) {
        /* use default */
      } finally {
        if (mounted) setReady(true);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useLayoutEffect(() => {
    applyThemeToDocument(activeThemeId);
  }, [activeThemeId]);

  const setUserTheme = useCallback((themeId) => {
    if (!isThemeId(themeId)) return;
    setUserThemeId(themeId);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, themeId);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const clearUserTheme = useCallback(() => {
    setUserThemeId(null);
    try {
      localStorage.removeItem(THEME_STORAGE_KEY);
    } catch (_) {
      /* ignore */
    }
  }, []);

  const setPlatformTheme = useCallback((themeId) => {
    if (!isThemeId(themeId)) return;
    setPlatformThemeId(themeId);
    cachePlatformTheme(themeId);
  }, []);

  const setPreviewTheme = useCallback((themeId) => {
    if (!isThemeId(themeId)) return;
    setPreviewThemeId(themeId);
  }, []);

  const clearPreviewTheme = useCallback(() => {
    setPreviewThemeId(null);
  }, []);

  const value = useMemo(
    () => ({
      ready,
      activeThemeId,
      previewThemeId,
      userThemeId,
      platformThemeId,
      isUsingPlatformDefault: !userThemeId,
      setUserTheme,
      clearUserTheme,
      setPlatformTheme,
      setPreviewTheme,
      clearPreviewTheme,
    }),
    [
      ready,
      activeThemeId,
      previewThemeId,
      userThemeId,
      platformThemeId,
      setUserTheme,
      clearUserTheme,
      setPlatformTheme,
      setPreviewTheme,
      clearPreviewTheme,
    ]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return {
      ready: true,
      activeThemeId: DEFAULT_THEME_ID,
      previewThemeId: null,
      userThemeId: null,
      platformThemeId: DEFAULT_THEME_ID,
      isUsingPlatformDefault: true,
      setUserTheme: () => {},
      clearUserTheme: () => {},
      setPlatformTheme: () => {},
      setPreviewTheme: () => {},
      clearPreviewTheme: () => {},
    };
  }
  return ctx;
};

export default ThemeContext;
